import React, { useEffect, useRef, useState } from 'react';
import { Room, RoomEvent, Track, createLocalTracks } from 'livekit-client';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Users, Mic, MicOff, Video, VideoOff, Square, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = ['video/mp4', 'video/webm;codecs=vp8,opus', 'video/webm'];
  for (const m of candidates) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* noop */ }
  }
  return null;
}

const CHUNK_SIZE = 4 * 1024 * 1024;

/**
 * Full-screen broadcaster modal for Thirrsty Go Live.
 *
 * Props:
 *  - open (bool)               : controls dialog visibility
 *  - session ({url,token,room,venue_name}) : from POST /api/live/start
 *  - onStop ()                 : called when the broadcaster ends the stream
 */
export function LiveBroadcasterDialog({ open, session, onStop }) {
  const [status, setStatus] = useState('connecting');
  const [viewers, setViewers] = useState(0);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [saving, setSaving] = useState(false);
  const roomRef = useRef(null);
  const videoRef = useRef(null);
  const camTrackRef = useRef(null);
  const micTrackRef = useRef(null);
  const recorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const recMimeRef = useRef(null);

  useEffect(() => {
    if (!open || !session?.token || !session?.url || !session?.room) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const room = new Room({ adaptiveStream: true, dynacast: true });
        roomRef.current = room;
        const syncViewers = () => setViewers(room.remoteParticipants.size);
        room.on(RoomEvent.ParticipantConnected, syncViewers);
        room.on(RoomEvent.ParticipantDisconnected, syncViewers);
        await room.connect(session.url, session.token);
        if (cancelled) { await room.disconnect(); return; }
        syncViewers();

        const tracks = await createLocalTracks({
          audio: true,
          video: { facingMode: 'user' },
        });
        for (const t of tracks) {
          if (t.kind === Track.Kind.Video) camTrackRef.current = t;
          else if (t.kind === Track.Kind.Audio) micTrackRef.current = t;
          await room.localParticipant.publishTrack(t);
        }
        if (camTrackRef.current && videoRef.current) {
          camTrackRef.current.attach(videoRef.current);
        }
        // Record locally for the VOD replay
        try {
          const mime = pickRecorderMime();
          if (mime && camTrackRef.current) {
            const streamTracks = [camTrackRef.current.mediaStreamTrack];
            if (micTrackRef.current) streamTracks.push(micTrackRef.current.mediaStreamTrack);
            const rec = new MediaRecorder(new MediaStream(streamTracks), { mimeType: mime });
            recChunksRef.current = [];
            recMimeRef.current = mime;
            rec.ondataavailable = (ev) => { if (ev.data?.size) recChunksRef.current.push(ev.data); };
            rec.start(3000);
            recorderRef.current = rec;
          }
        } catch (recErr) {
          // Recording is best-effort; the live stream itself is unaffected
          console.warn('[VOD] recorder unavailable', recErr);
        }
        setStatus('live');
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[LiveKit] broadcaster failed', e);
        toast.error(e?.message || 'Could not access camera / mic');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
      try { if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop(); } catch (e) { /* noop */ }
      try { camTrackRef.current?.stop(); } catch (e) { /* noop */ }
      try { micTrackRef.current?.stop(); } catch (e) { /* noop */ }
      try { roomRef.current?.disconnect(); } catch (e) { /* noop */ }
      camTrackRef.current = null;
      micTrackRef.current = null;
      roomRef.current = null;
    };
  }, [open, session]);

  const uploadReplay = async () => {
    const rec = recorderRef.current;
    if (!rec) return;
    recorderRef.current = null;
    if (rec.state !== 'inactive') {
      await new Promise((resolve) => { rec.onstop = resolve; try { rec.stop(); } catch { resolve(); } });
    }
    const blob = new Blob(recChunksRef.current, { type: recMimeRef.current });
    recChunksRef.current = [];
    if (blob.size < 100 * 1024) return; // too short to be worth saving
    setSaving(true);
    try {
      const { data } = await api.post('/vod/upload/start', {
        venue_name: session?.venue_name || null,
        mime_type: recMimeRef.current,
        total_size: blob.size,
      });
      let index = 0;
      for (let offset = 0; offset < blob.size; offset += CHUNK_SIZE, index++) {
        const chunk = blob.slice(offset, offset + CHUNK_SIZE);
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/vod/upload/${data.vod_id}/chunk?chunk_index=${index}`, chunk, {
          headers: { 'Content-Type': 'application/octet-stream' },
        });
      }
      await api.post(`/vod/upload/${data.vod_id}/complete`);
      toast.success('Replay saved! It now shows in Replays on the timeline.');
    } catch (e) {
      toast.error('Could not save the replay, but your live stream was fine.');
    } finally {
      setSaving(false);
    }
  };

  const handleEnd = async () => {
    if (saving) return;
    await uploadReplay();
    onStop?.();
  };

  const toggleMic = () => {
    const t = micTrackRef.current;
    if (!t) return;
    if (micOn) t.mute(); else t.unmute();
    setMicOn(!micOn);
  };
  const toggleCam = () => {
    const t = camTrackRef.current;
    if (!t) return;
    if (camOn) t.mute(); else t.unmute();
    setCamOn(!camOn);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleEnd(); }}>
      <DialogContent
        className="max-w-md p-0 border-white/10 bg-[#07070A] text-white overflow-hidden"
        data-testid="live-broadcaster-dialog"
      >
        <div className="relative">
          <video
            ref={videoRef}
            playsInline muted autoPlay
            className="w-full aspect-[9/16] bg-black object-cover"
            data-testid="live-broadcaster-video"
          />
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {status === 'live' && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider">
                <span className="inline-flex h-2 w-2 rounded-full bg-white th-live-dot" /> Live
              </span>
            )}
            {status === 'connecting' && (
              <span className="rounded-full bg-white/10 px-2.5 py-1 text-[10px] uppercase tracking-wider">Connecting…</span>
            )}
            {status === 'error' && (
              <span className="rounded-full bg-red-600/80 px-2.5 py-1 text-[10px] uppercase tracking-wider">Camera blocked</span>
            )}
            {session?.venue_name && (
              <span className="rounded-full bg-black/60 px-2.5 py-1 text-[11px]">at {session.venue_name}</span>
            )}
          </div>
          <div
            className="absolute top-3 right-3 rounded-full bg-black/60 px-2.5 py-1 text-xs inline-flex items-center gap-1"
            data-testid="live-broadcaster-viewers"
          >
            <Users className="h-3.5 w-3.5" /> {viewers}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 p-3 border-t border-white/10 bg-[#0B0C10]">
          <Button
            type="button" variant="outline" onClick={toggleMic}
            className="rounded-full border-white/12 bg-white/5 text-white hover:bg-white/10"
            data-testid="live-broadcaster-mic-toggle"
          >
            {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4 text-red-400" />}
          </Button>
          <Button
            type="button" variant="outline" onClick={toggleCam}
            className="rounded-full border-white/12 bg-white/5 text-white hover:bg-white/10"
            data-testid="live-broadcaster-cam-toggle"
          >
            {camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4 text-red-400" />}
          </Button>
          <Button
            type="button" onClick={handleEnd} disabled={saving}
            className="flex-1 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold"
            data-testid="live-broadcaster-stop"
          >
            {saving
              ? (<><Loader2 className="mr-2 h-4 w-4 th-spin" /> Saving replay…</>)
              : (<><Square className="mr-2 h-4 w-4 fill-current" /> End Live</>)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default LiveBroadcasterDialog;
